import React, { useCallback, useState } from 'react';

/**
 * Copies `url` to the clipboard when clicked and shows brief feedback.
 * Falls back to a hidden <textarea> + execCommand when the async Clipboard API
 * is unavailable (older browsers, non-secure contexts).
 */
async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {
    // fall through to the legacy path
  }

  try {
    const helper = document.createElement('textarea');
    helper.value = text;
    helper.setAttribute('readonly', '');
    helper.style.position = 'absolute';
    helper.style.left = '-9999px';
    document.body.appendChild(helper);
    helper.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(helper);
    return ok;
  } catch (_) {
    return false;
  }
}

function CopyLinkButton({ url, label = 'Copy link', className = '' }) {
  const [state, setState] = useState('idle'); // idle | copied | error

  const handleClick = useCallback(async () => {
    const ok = await copyToClipboard(url);
    setState(ok ? 'copied' : 'error');
    window.setTimeout(() => setState('idle'), 2000);
  }, [url]);

  const text =
    state === 'copied'
      ? '✓ Link copied'
      : state === 'error'
        ? 'Copy failed — select and copy manually'
        : `🔗 ${label}`;

  return (
    <button
      type="button"
      className={`copy-link-btn${className ? ` ${className}` : ''}`}
      onClick={handleClick}
      title={url}
      aria-label={`${label}: ${url}`}
    >
      {text}
    </button>
  );
}

export default CopyLinkButton;
